/**
 * The parked-question model (D79) — questions are STATE the player can see,
 * and they survive until answered, replaced, or left behind.
 *
 * @remarks
 * D37 gave the engine answerable questions and D45 gave them manners; D79
 * gives them a body. A question — a completion ("Open it with what?") or the
 * parser's which-one ask — lives in `RuntimeState.session.pendingQuestion`,
 * carrying its own text (resolved at park time, the R15 pattern), the CLAUSE
 * that asked it (F5), the room it belongs to, and the asking input's ordinal
 * as its identity. Because it is state: saves resume the conversation,
 * replays re-derive it, and the UI renders the question from the same truth
 * the engine answers against.
 *
 * Survival (Mike's rulings, 2026-08-23; amended R33/R34): the question
 * outlives noise, unresolved answers, AND executed commands — "she waits" —
 * until it is answered (a RESOLVED answer closes it whatever the outcome,
 * D113), a newer question replaces it (D66's preference, generalized), the
 * player leaves the room it was asked in (`questionWithdrawn`, one line,
 * once), or the player withdraws it themselves (`never mind` — R34/D114,
 * `withdraw-question.ts`). D235: an ACTOR's question (the `dialogue` kind,
 * `conversation/prompts.ts`) lives under the same rules, and leaves its
 * room in the actor's own words (the prompt's `unansweredNarratives`) when
 * it authors them. Since 2026-09-28 (Mike's ruling) a which-one
 * question also closes, silently, once the world has settled it — its clause
 * re-read against the room no longer asks. D225 (2026-10-02): a question the
 * engine volunteered unprinted (the bare-noun offer) leaves its room without
 * the withdrawal line, and a turn that closes the world with a terminal
 * ending closes every question silently. UI dismissal is not modeled here because it no
 * longer exists as its own thing: the chip's dismiss SUBMITS the withdrawal
 * words, so replays fold raw inputs only and still carry the gesture.
 *
 * Which-one resolution is the module's second job, and it does NO text
 * surgery: an answer that names a candidate re-parses the ORIGINAL clause
 * against a catalog with the LOSING candidates removed — the ambiguity
 * vanishes and the parser stays the judge, exactly as it was for the
 * original input. The candidate match itself reuses the D37/D64 bare-noun
 * reading, restricted to the offered candidates. D247: what the answer rules
 * out outlives it — a question the settled clause asks next carries it
 * (`clause-settlement.ts`).
 *
 * @packageDocumentation
 */

import type { NarrativeLine, RuntimeState } from '@orpheus/content-schema';

import { type ClarificationRequest, type EntityRef, foldCharacters, parseCommand } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireActor, requirePrompt } from '../bundle/bundle-lookups.js';
import { bumpResponseUsage, speakSlot } from '../defaults/default-responses.js';
import { isActorAddressable } from '../entities/actor-presence.js';
import { deriveEntityCatalog } from '../entities/derive-entity-catalog.js';
import { describeEntityRef } from '../entities/describe-entity-ref.js';
import { terminalEndingOf } from '../narrative/session-ended.js';
import { splitSpokenLines } from './action-handler-result.js';
import { clauseCatalogOf } from './clause-settlement.js';
import { buildParseContext } from './parse-context.js';
import { type PlayerInputOutcome } from './process-player-input.js';
import { appendTranscript, inputNamespaceCursor, stampNarrativeLines } from './transcript-cursor.js';

/** Any parked question (mirrors the schema's union). */
export type PendingQuestion = NonNullable<RuntimeState['session']['pendingQuestion']>;

/** The completion kind — D37's shape, D79-embodied. */
export type PendingCompletionQuestion = Extract<PendingQuestion, { kind: 'completion' }>;

/** The which-one kind — the parser's clarification, parked. */
export type PendingWhichEntityQuestion = Extract<PendingQuestion, { kind: 'which_entity' }>;

/** D235: the dialogue kind — an actor's question, with the answers it offered. */
export type PendingDialogueQuestion = Extract<PendingQuestion, { kind: 'dialogue' }>;

/**
 * What one input was to the question parked before it: its `answer`; a
 * command the player `moved_on` with, under a completion (D245); or
 * `neither` — noise, a miss, anything the question waits through (D79).
 */
export type QuestionReply = 'answer' | 'moved_on' | 'neither';

/** The state with a question parked on it. */
export const parkQuestion = (state: RuntimeState, question: PendingQuestion): RuntimeState => ({
	...state,
	session: { ...state.session, pendingQuestion: question },
});

/** The state with no question parked (rest-sibling omission drops it). */
export const clearQuestion = (state: RuntimeState): RuntimeState => {
	if (state.session.pendingQuestion === undefined) {
		return state;
	}

	const { pendingQuestion: _droppedQuestion, ...sessionWithoutQuestion } = state.session;

	return { ...state, session: sessionWithoutQuestion };
};

/**
 * Build the parked form of a parser clarification.
 *
 * @param clarification - The parser's ask
 * @param askedAtInputOrdinal - The asking input's ordinal (the question's identity)
 * @param questionText - The ask's SPOKEN text (R10/D80: the caller speaks the line into the transcript and parks the same words — resolved once, said once, remembered once)
 * @param roomId - The room the question is asked in
 * @returns The which-one question, ready to park
 */
export const questionForClarification = (
	clarification: ClarificationRequest,
	askedAtInputOrdinal: number,
	questionText: string,
	roomId: RuntimeState['progression']['activeRoomId'],
): PendingWhichEntityQuestion => ({
	askedAtInputOrdinal,
	candidates: clarification.candidates.map((candidate) => ({
		entityRef: candidate.entityRef,
		matchedTokens: candidate.matchedTokens,
	})),
	clauseText: clarification.clauseText,
	kind: 'which_entity',
	questionText,
	roomId,
});

/**
 * The candidate a which-one answer names, if it names exactly one.
 *
 * @remarks
 * The answer is read as a noun phrase (the D37/D64 bare-noun reading) against
 * a catalog RESTRICTED to the offered candidates: "gray", "the gray rag", and
 * the full name all resolve; an answer naming something else entirely returns
 * `undefined` and falls through to ordinary processing under the survival
 * rules (a bare noun describes its thing and asks the D37 follow-up, which
 * REPLACES this question — D66's newest-question preference, emerging rather
 * than being coded twice). An answer that still ties among the candidates
 * also returns `undefined`: the question simply stands.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the answer
 * @param rawInput - The player's reply, verbatim (typed or chip-submitted)
 * @param question - The parked which-one question
 * @returns The chosen candidate's ref, or `undefined` when nothing resolved uniquely
 */
export const whichEntityAnswerRef = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	rawInput: string,
	question: PendingWhichEntityQuestion,
): EntityRef | undefined => {
	if (rawInput.trim() === '' || (!isAnOfferedAnswer(bundle, rawInput, question) && readsAsItsOwnCommand(bundle, state, rawInput))) {
		return undefined;
	}

	const candidateRefs = new Set(question.candidates.map((candidate) => candidate.entityRef));
	const candidateCatalog = deriveEntityCatalog(bundle, state).filter((entity) =>
		candidateRefs.has(entity.entityRef as unknown as string),
	);

	// The noun-phrase reading needs an `inspect` verb to borrow (the D37
	// bare-noun pattern); a vocabulary without one just skips to the
	// qualifier match below, which needs no verb at all.
	if (bundle.compiledLexicon.intentsByToken.has('inspect')) {
		const outcome = parseCommand(`inspect ${rawInput}`, buildParseContext(bundle, candidateCatalog));

		if (outcome.type === 'parsed' && outcome.commands[0]?.directObjectRef !== undefined) {
			return outcome.commands[0].directObjectRef;
		}
	}

	/*
	 * The QUALIFIER-only answer ("sealed", "the gray one"): the candidates tied
	 * on their shared noun, so the word that answers a which-one question is
	 * usually the word that is NOT a noun — and a bare adjective is not a noun
	 * phrase, so the parser reading above declines it. Matched here instead,
	 * deterministically: fold the answer, drop articles, and a candidate is
	 * chosen when it is the UNIQUE one whose own words (nouns, aliases,
	 * adjectives) cover every answer word. Two matches is still a tie; zero is
	 * not an answer. No fuzzing — a misspelled qualifier falls through to the
	 * ordinary teach, question standing.
	 */
	const answerWords = foldCharacters(rawInput)
		.split(/\s+/u)
		.filter((word) => word !== '' && word !== 'a' && word !== 'an' && word !== 'one' && word !== 'the');

	if (answerWords.length === 0) {
		return undefined;
	}

	const coveringCandidates = candidateCatalog.filter((entity) => {
		const candidateWords = new Set(
			[...entity.nouns, ...entity.aliases, ...entity.adjectives].flatMap((word) => foldCharacters(word).split(/\s+/u)),
		);

		return answerWords.every((word) => candidateWords.has(word));
	});

	return coveringCandidates.length === 1 ? coveringCandidates[0]?.entityRef : undefined;
};

/**
 * Is this reply one of the answers the question OFFERED, word for word — a
 * chip's own label, or the player typing it back?
 *
 * @remarks
 * A chip submits its label as the player's words (D86), and a label can
 * begin with a word the vocabulary knows as a verb — the fixture's
 * `open crate`, a pack's `seal` or `lock`. The offered names are answers by
 * construction, so they are never mistaken for commands of their own.
 *
 * @param bundle - The loaded bundle
 * @param rawInput - The player's reply, verbatim
 * @param question - The parked which-one question
 * @returns Whether the reply is an offered answer's exact text
 */
const isAnOfferedAnswer = (bundle: EngineGameBundle, rawInput: string, question: PendingWhichEntityQuestion): boolean =>
	question.candidates.some(
		(candidate) => foldCharacters(describeEntityRef(bundle, candidate.entityRef as unknown as EntityRef)) === foldCharacters(rawInput),
	);

/**
 * Is this reply a command of its own — a clause the grammar read with a verb
 * the player typed — rather than a name offered as an answer?
 *
 * @remarks
 * An answer to "which?" is a NAME: `the widow's`, `the sealed one`,
 * `eleven`. The answer reading borrows a verb (`inspect <reply>`), so a reply
 * that brought its own verb was read with two, and since D216 the second one
 * can describe — in Fever Ground's jar house `open <a named jar>` read as
 * `inspect open <that jar>`, the open jar was that one, and the parked
 * `examine open jar` ran on it: the player asked to open a jar and was shown
 * one. Mike found the same
 * leftover question under the lockers. A reply the parser reads as a whole
 * command against the room, with a verb it did not have to rescue, is the
 * player moving on: it runs as itself, and the question waits (D79's
 * survival — it outlives executed commands).
 *
 * A reply the parser only reads as a bare noun (`the widow's jar`), or cannot
 * read at all (`sealed`, `the one`), is still offered to the answer reading.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the reply
 * @param rawInput - The player's reply, verbatim
 * @returns Whether the reply is a command in its own right
 */
const readsAsItsOwnCommand = (bundle: EngineGameBundle, state: RuntimeState, rawInput: string): boolean => {
	const outcome = parseCommand(rawInput, buildParseContext(bundle, deriveEntityCatalog(bundle, state)));

	return outcome.type === 'parsed' && outcome.commands.some((command) => command.rescuedAs !== 'bare_noun');
};

/**
 * Apply D79's survival rules to a finished outcome: park a fresh which-one
 * ask, keep or withdraw the previous question, and never guess.
 *
 * @remarks
 * Precedence, in order: a question THIS input earned wins (a completion the
 * ladder parked, or the clarification parked here — D66's newest-question
 * preference, generalized); an ANSWERED question is done; a `session_ended`
 * world keeps no questions, and neither does a world this turn closed with a
 * terminal ending (D225); otherwise the previous question survives — unless
 * the player has LEFT its room, which is context death, closed loudly with
 * the one `questionWithdrawn` line (Mike's D79 ruling) — or silently, when
 * the question was the engine's own unprinted offer (D225) — or (D245) the
 * input was a command of the player's own under a completion, which lets go
 * without a word wherever it leaves them standing (a command that walks
 * them out of the room is still context death, and still says so).
 *
 * @param bundle - The loaded bundle
 * @param parkedQuestion - The question that was open BEFORE this input, if any
 * @param reply - What this input was to it: its answer, a command the player moved on with (D245), or neither
 * @param outcome - The finished outcome (the ladder's parks already applied)
 * @returns The outcome with the surviving question settled
 */
export const applyQuestionSurvival = (
	bundle: EngineGameBundle,
	parkedQuestion: PendingQuestion | undefined,
	reply: QuestionReply,
	outcome: PlayerInputOutcome,
): PlayerInputOutcome => {
	if (outcome.type === 'clarification_required') {
		/*
		 * R10 (Calder's amendment, D80): the ask PRINTS as well as taking the
		 * surface — the arm spoke it, ordered BEFORE the §8.8 coda (D81, cause
		 * → consequence). The park reads the SPOKEN text off its provenance
		 * stamp, exactly as `questionForMissingObject` reads the teach — one
		 * resolution, said once, remembered once, so surface and record can
		 * never disagree.
		 */
		/*
		 * UNREACHABLE `?? ''`. This park runs only on the arm that has just STAMPED
		 * the ask into `outcome.transcriptEntries` under a `question_asked` trigger
		 * context — that stamping is the reason the park reads the text from
		 * provenance instead of re-rendering it (D80: surface and record can never
		 * disagree because they are the same string). A park with no stamped ask
		 * above it would be the seam already broken.
		 *
		 * `findLast` returns `T | undefined` regardless, so the default is a
		 * type-level obligation rather than a runtime path.
		 *
		 * Verified 2026-08-23: a throwing default leaves the whole repo suite green.
		 */
		/* v8 ignore next 2 */
		const questionText =
			outcome.transcriptEntries.findLast((entry) => entry.triggerContext?.startsWith('question_asked') === true)?.text ?? '';

		return {
			...outcome,
			nextState: parkQuestion(
				outcome.nextState,
				questionForClarification(
					outcome.clarification,
					outcome.nextState.session.commandHistory.length,
					questionText,
					outcome.nextState.progression.activeRoomId,
				),
			),
		};
	}

	const hasFreshQuestion =
		outcome.type === 'executed'
			? outcome.result.nextState.session.pendingQuestion !== undefined
			: outcome.type !== 'session_ended' && outcome.nextState.session.pendingQuestion !== undefined;

	if (parkedQuestion === undefined || reply === 'answer' || hasFreshQuestion || outcome.type === 'session_ended') {
		return outcome;
	}

	if (outcome.type !== 'executed') {
		return reply === 'moved_on' ? outcome : { ...outcome, nextState: parkQuestion(outcome.nextState, parkedQuestion) };
	}

	/*
	 * D225 (E4): a turn that CLOSED the world closes its questions with it,
	 * silently. Drumline's `drum`, then `south` into the ending, spoke the
	 * epilogue, the ending's stamp — and then "The question stays behind with
	 * the room that asked it", the engine withdrawing a conversation from a
	 * world that had just ended. A closed world declines every later input at
	 * the terminal gate, so a question parked on it could never be answered;
	 * it is dead state, and the ending has the last word.
	 */
	if (terminalEndingOf(bundle, outcome.result.nextState) !== undefined) {
		return outcome;
	}

	const didMoveAway = outcome.result.nextState.progression.activeRoomId !== parkedQuestion.roomId;

	if (!didMoveAway) {
		// Mike's ruling, 2026-09-28: a which-one question the world has since
		// settled closes itself, silently — its chips would offer a choice that
		// no longer exists (see isSettledByTheWorld).
		return {
			...outcome,
			result: {
				...outcome.result,
				nextState:
					reply === 'moved_on' || isSettledByTheWorld(bundle, outcome.result.nextState, parkedQuestion)
						? outcome.result.nextState
						: parkQuestion(outcome.result.nextState, parkedQuestion),
			},
		};
	}

	/*
	 * D225 (E4): a question the engine VOLUNTEERED — D37's bare-noun offer,
	 * parked unprinted by D84's ruling — leaves with the room and says
	 * nothing. The withdrawal line below is for a question the player's own
	 * input created and the record carries; Salt Debt's `salt`, then `south`
	 * printed "The question stays behind with the room that asked it" under a
	 * record in which no question had been asked, the engine talking about
	 * itself exactly where D84 said it must not.
	 */
	if (parkedQuestion.kind === 'completion' && parkedQuestion.volunteered === true) {
		return outcome;
	}

	// Context death: the question was asked in a room the player has left.
	// One line, once, in the input's own namespace (the bare-noun pattern).
	// D235: an actor's question leaves in the actor's words when the prompt
	// authors them — "Go on, then. I am not going anywhere." — else the slot.
	const { nextState, transcriptEntries } = outcome.result;
	const authoredLines = parkedQuestion.kind === 'dialogue' ? unansweredLinesOf(bundle, parkedQuestion) : undefined;
	const { lines, spokenSlots } = splitSpokenLines(authoredLines ?? [speakSlot(bundle, nextState, 'questionWithdrawn')]);
	const stamped = stampNarrativeLines(
		bundle,
		nextState,
		inputNamespaceCursor(nextState.session.commandHistory.length, transcriptEntries.length, nextState),
		lines,
		`question_withdrawn:${String(parkedQuestion.askedAtInputOrdinal)}`,
	);
	const withdrawnState = appendTranscript(nextState, stamped.entries);

	return {
		...outcome,
		result: {
			...outcome.result,
			nextState: {
				...withdrawnState,
				session: {
					...withdrawnState.session,
					responseUsage: bumpResponseUsage(withdrawnState.session.responseUsage, spokenSlots),
				},
			},
			transcriptEntries: [...transcriptEntries, ...stamped.entries],
		},
	};
};

/**
 * D235: what the actor says when its question is walked away from — the
 * prompt's `unansweredNarratives`, when it authors them.
 *
 * @param bundle - The loaded bundle
 * @param question - The dialogue question being withdrawn
 * @returns The actor's lines, or `undefined` when the engine's slot speaks
 */
const unansweredLinesOf = (bundle: EngineGameBundle, question: PendingDialogueQuestion): undefined | readonly NarrativeLine[] =>
	requirePrompt(requireActor(bundle, question.actorId), question.promptId).unansweredNarratives;

/**
 * Has the world settled this question since it was asked — does its clause,
 * read again against the room as it is now, no longer need to ask?
 *
 * @remarks
 * Mike's ruling, 2026-09-28, amending D79's survival: `examine open locker`
 * asked which while both lockers stood open, and after twelve was shut the
 * chips still offered both, though one open locker was left. A which-one
 * question is a CHOICE, and once the world leaves one answer (or none) the
 * choice is gone, so the question closes without a word — the player never
 * asked for it to be withdrawn, and there is nothing to announce. A question
 * whose clause still asks keeps waiting, as D79 rules. Only executed turns in
 * the question's own room reach here: a refusal changes nothing, and leaving
 * the room closes a question aloud.
 *
 * D230 reads a completion by the same light, for the one change that settles
 * it: the THING it asked about is gone. "Where, or on what?" parked for
 * `leave coin`, and then `put coin on well` ran as the command it is (D230,
 * the page's verbs); the coin was on the ledge, the question still stood,
 * and the next bare noun the player typed — `well`, to look at it — was
 * spliced into a question about a coin they no longer had: "You see no coin
 * here." A completion asks for a missing object, which no change in the
 * room supplies, so it is otherwise never settled this way; but a clause
 * that no longer names anything the player can refer to is a question about
 * nothing, and closes without a word. The same reading covers the
 * volunteered offer ("The coin, then. What would you like done with it?")
 * once the coin is gone.
 *
 * @param bundle - The loaded bundle
 * @param state - The state after the turn
 * @param question - The parked question
 * @returns Whether the question no longer offers a real choice
 */
const isSettledByTheWorld = (bundle: EngineGameBundle, state: RuntimeState, question: PendingQuestion): boolean => {
	// D235: an actor's question has no clause to re-read; what settles it is
	// the actor leaving earshot — moved off, or hidden again — because chips
	// offering answers to someone who can no longer hear them offer a choice
	// that no longer exists. Walking away yourself is the loud exit above.
	if (question.kind === 'dialogue') {
		return !isActorAddressable(bundle, state, question.actorId);
	}

	// D247: read as the player settled it — without what a which-one answer ruled out.
	const ruledOutRefs = 'ruledOutRefs' in question ? question.ruledOutRefs : undefined;
	const rereadClause = parseCommand(question.clauseText, buildParseContext(bundle, clauseCatalogOf(bundle, state, ruledOutRefs)));

	if (question.kind === 'which_entity') {
		return rereadClause.type !== 'needs_clarification';
	}

	return rereadClause.type === 'unrecognized' && rereadClause.diagnostics.failureKind === 'entity_not_found';
};
